// Modern JSX transform - React import not needed
import { useEffect, memo, cloneElement } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline'
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout'
import { useImageGallery } from '../../hooks/useImageGallery'
import debugWrapper from '../../utils/debugWrapper'
import ImmersivePanelHeader from './ImmersivePanelHeader'
import PanelBannerSlot from './PanelBannerSlot'
import ImmersiveTabContent from './ImmersiveTabContent'

/**
 * ImmersiveMapPanelContent - Centralized panel content renderer for immersive map pages
 * Orchestrates tab navigation, panel state, and delegates content rendering to specialized components
 * 
 * Reduced from 791 lines to orchestrator pattern - extracts:
 * - EntityDisplayPanel (entity detail display with gallery, favorite, share)
 * - ImmersiveTabContent (tab-specific content delegation)
 */
const ImmersiveMapPanelContent = memo(function ImmersiveMapPanelContent({
  // Panel state
  panelState,
  activeTab,
  onTabChange,
  onPanelStateChange,
  
  // Header props
  immersiveTabs,
  user,
  onLogout,
  initialChannelId,
  onChannelSelected,
  onUnreadCountChange,
  claimPrefill,
  onClaimPrefillConsumed,
  
  // Search tab props
  searchLocation,
  onSearchLocationChange,
  radius,
  onRadiusChange,
  onGeocode,
  onCurrentLocation,
  onSpotClick,
  isGeocoding,
  locationSource,
  entitySearchQuery,
  onEntitySearchChange,
  entitySearchInputRef,
  isSearching,
  searchResults,
  showDropdown,
  onSelectEntity,
  onCloseDropdown,
  userLocation,
  searchLocationName,
  entityCount,
  isLoading,
  onShare,
  
  // Filters (for search tab)
  entities = [],
  activeFilters = [],
  onFilterToggle = null,
  openNowActive = false,
  onToggleOpenNow = null,
  showEvents = true,
  onToggleShowEvents = null,
  
  // Featured entity props
  featuredEntity = null,
  showingFeaturedEntity = false,
  featuredEntityReason = null,
  onFeaturedViewDetails = null,
  onFeaturedShowOnMap = null,
  onFeaturedJoinChannel = null,
  
  // Auth panel props
  stats,
  statsLoading,
  pendingConfirmations,
  loadingConfirmations,
  confirmingId,
  handleConfirmSighting,
  loginFormData,
  loginErrors,
  handleLoginChange,
  handleLoginSubmit,
  registerFormData,
  registerErrors,
  handleRegisterChange,
  handleRegisterSubmit,
  isAuthLoading,
  googleLoginUrl,
  
  // Contact tab props (handled internally by CompactChannelsView)
  
  // Entity-specific (for EntityDetailMap)
  isEntityMode = false,
  entityDetails = null,
  latestSighting = null,
  entityGallery = [],
  galleryRenderKey = 0,
  entityId = null,
  toggleFavorite = null,
  isFavorite = null,
  isTogglingFavorite = false,
  entityNotifyOnSpot = false,
  onEntityNotifyToggle = null,
  getCategoryIcon = null,
  getCategoryColor = null,
  getCategoryIconColor = null,
  canEditEntity = false,
  onNavigateToEdit = null,
  onConfirmLocationClick = null,
  
  // Optional props
  searchSortBy = 'recent',
  onSearchSortByChange = null,
  entityName = null,
  entityCategory = null,
  className = ''
}) {
  // Use image gallery hook for entity mode
  const {
    selectedImage,
    selectedImageIndex,
    handleImageClick,
    handleCloseImage,
    handlePreviousImage,
    handleNextImage
  } = useImageGallery(entityGallery)
  
  // Refresh unread count when contact tab becomes active
  useEffect(() => {
    if (activeTab === 'contact' && onUnreadCountChange) {
      onUnreadCountChange()
    }
  }, [activeTab, onUnreadCountChange])

  // Contact tab has no semi state — bump up to expanded immediately
  useEffect(() => {
    if (activeTab === 'contact' && panelState === 'semi' && onPanelStateChange) {
      onPanelStateChange('expanded')
    }
  }, [activeTab, panelState, onPanelStateChange])

  const { isDesktop } = useResponsiveLayout()

  const effectivePanelState = (activeTab === 'contact' && panelState === 'semi') ? 'expanded' : panelState
  const isCollapsed = effectivePanelState === 'minimized' || effectivePanelState === 'hidden'

  // Toggle bottom sheet open/closed on drag handle tap
  const handleDragHandleTap = () => {
    if (!onPanelStateChange) return
    onPanelStateChange(effectivePanelState === 'minimized' ? 'expanded' : 'minimized')
  }

  // Shared banner + content — rendered in both mobile and desktop expanded paths
  const sharedBanner = effectivePanelState !== 'minimized' && (
    <div className="flex-shrink-0">
      <PanelBannerSlot
        onTabChange={onTabChange}
        onSpotClick={onSpotClick}
        featuredEntity={featuredEntity}
        showingFeaturedEntity={showingFeaturedEntity}
        featuredEntityReason={featuredEntityReason}
        onFeaturedViewDetails={onFeaturedViewDetails}
        onFeaturedShowOnMap={onFeaturedShowOnMap}
        onFeaturedJoinChannel={onFeaturedJoinChannel}
      />
    </div>
  )

  const sharedContent = effectivePanelState !== 'minimized' && (
    <div className={`flex-1 min-h-0 ${activeTab === 'contact' || activeTab === 'vendor' ? 'flex flex-col overflow-hidden pt-1' : activeTab === 'live' ? 'flex flex-col overflow-hidden' : 'p-2 space-y-1 overflow-x-clip panel-scroll-hide backdrop-blur-sm overflow-y-auto'}`}>
      <ImmersiveTabContent
        activeTab={activeTab}
        onTabChange={onTabChange}
        isEntityMode={isEntityMode}
        entityDetails={entityDetails}
        latestSighting={latestSighting}
        entityGallery={entityGallery}
        galleryRenderKey={galleryRenderKey}
        entityId={entityId}
        panelState={effectivePanelState}
        onPanelStateChange={onPanelStateChange}
        user={user}
        toggleFavorite={toggleFavorite}
        isFavorite={isFavorite}
        isTogglingFavorite={isTogglingFavorite}
        entityNotifyOnSpot={entityNotifyOnSpot}
        onEntityNotifyToggle={onEntityNotifyToggle}
        getCategoryIcon={getCategoryIcon}
        getCategoryColor={getCategoryColor}
        getCategoryIconColor={getCategoryIconColor}
        canEditEntity={canEditEntity}
        onNavigateToEdit={onNavigateToEdit}
        onConfirmLocationClick={onConfirmLocationClick}
        selectedImage={selectedImage}
        selectedImageIndex={selectedImageIndex}
        onImageClick={handleImageClick}
        onCloseImage={handleCloseImage}
        onPreviousImage={handlePreviousImage}
        onNextImage={handleNextImage}
        searchLocation={searchLocation}
        onSearchLocationChange={onSearchLocationChange}
        radius={radius}
        onRadiusChange={onRadiusChange}
        onGeocode={onGeocode}
        onCurrentLocation={onCurrentLocation}
        onSpotClick={onSpotClick}
        isGeocoding={isGeocoding}
        locationSource={locationSource}
        entitySearchQuery={entitySearchQuery}
        onEntitySearchChange={onEntitySearchChange}
        entitySearchInputRef={entitySearchInputRef}
        isSearching={isSearching}
        searchResults={searchResults}
        showDropdown={showDropdown}
        onSelectEntity={onSelectEntity}
        onCloseDropdown={onCloseDropdown}
        userLocation={userLocation}
        searchLocationName={searchLocationName}
        entityCount={entityCount}
        isLoading={isLoading}
        onShare={onShare}
        stats={stats}
        statsLoading={statsLoading}
        pendingConfirmations={pendingConfirmations}
        loadingConfirmations={loadingConfirmations}
        confirmingId={confirmingId}
        handleConfirmSighting={handleConfirmSighting}
        loginFormData={loginFormData}
        loginErrors={loginErrors}
        handleLoginChange={handleLoginChange}
        handleLoginSubmit={handleLoginSubmit}
        registerFormData={registerFormData}
        registerErrors={registerErrors}
        handleRegisterChange={handleRegisterChange}
        handleRegisterSubmit={handleRegisterSubmit}
        isAuthLoading={isAuthLoading}
        googleLoginUrl={googleLoginUrl}
        initialChannelId={initialChannelId}
        onChannelSelected={onChannelSelected}
        onUnreadCountChange={onUnreadCountChange}
        claimPrefill={claimPrefill}
        onClaimPrefillConsumed={onClaimPrefillConsumed}
        searchSortBy={searchSortBy}
        onSearchSortByChange={onSearchSortByChange}
        activeFilters={activeFilters}
        onFilterToggle={onFilterToggle}
        openNowActive={openNowActive}
        onToggleOpenNow={onToggleOpenNow}
        showEvents={showEvents}
        onToggleShowEvents={onToggleShowEvents}
      />
    </div>
  )

  // Mobile: bottom sheet slides up from the bottom — full-width, overlays the map
  if (!isDesktop) {
    const mobileHeightMap = { expanded: '78vh', semi: '45vh', minimized: '125px', hidden: '0px' }
    if (effectivePanelState === 'hidden') return null
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-[998] bg-white rounded-t-2xl flex flex-col overflow-hidden"
        style={{
          height: mobileHeightMap[effectivePanelState] ?? '125px',
          transition: 'height 300ms ease-out',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.10)',
          borderTop: 'none'
        }}
      >
        {/* Drag handle + logo — combined header, tap to toggle */}
        <div
          className="flex-shrink-0 flex flex-col items-center pt-2.5 pb-2 cursor-pointer"
          onClick={handleDragHandleTap}
          onTouchEnd={(e) => { e.preventDefault(); handleDragHandleTap() }}
        >
          <div className="w-10 h-1 bg-gray-300 rounded-full mb-3" />
          <Link
            to="/"
            className="flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <img
              src="/logo.png"
              alt="TrckIt"
              className={`w-auto h-[50px] transition-transform duration-700 ${isLoading ? 'animate-spin' : ''}`}
            />
          </Link>
          {entityName && effectivePanelState === 'minimized' && (
            <p className="text-xs font-semibold text-primary truncate max-w-[200px] mt-1">{entityName}</p>
          )}
        </div>

        {/* Tab bar */}
        <div className="flex-shrink-0 px-2 pb-1.5">
          {immersiveTabs}
        </div>

        {sharedBanner}
        {sharedContent}
      </div>
    )
  }

  // Desktop: rail mode — 64px icon column
  if (isCollapsed) {
    return (
      <div className="h-full w-16 flex flex-col items-center bg-white border-r border-gray-100 shadow-[4px_0_24px_rgba(0,0,0,0.06)] overflow-hidden">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center py-3">
          <img src="/logo.png" alt="TrckIt" className="w-8 h-8" />
        </Link>

        {/* Tab icons — vertical stack */}
        <div className="flex flex-col items-center gap-1 flex-1 py-1 overflow-y-auto w-full">
          {immersiveTabs && cloneElement(immersiveTabs, { isRail: true, onTabClick: (tabId) => {
            if (onTabChange) onTabChange(tabId, 'expanded')
          }})}
        </div>

        {/* Expand chevron */}
        <button
          className="mx-2 mb-3 flex items-center justify-center py-2 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
          aria-label="Expand panel"
          onClick={() => onPanelStateChange && onPanelStateChange('expanded')}
          onTouchEnd={(e) => { e.preventDefault(); onPanelStateChange && onPanelStateChange('expanded') }}
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>
    )
  }

  // Desktop: expanded drawer
  return (
    <div className={`h-full w-[360px] flex flex-col bg-white border-r border-gray-100 shadow-[4px_0_24px_rgba(0,0,0,0.06)] overflow-hidden ${className}`}>
      {effectivePanelState !== 'hidden' && (
        <>
          <div className="flex-shrink-0">
            <ImmersivePanelHeader
              isMinimized={false}
              immersiveTabs={immersiveTabs}
              user={user}
              onLogout={onLogout}
              isLoading={isLoading}
              onCollapse={() => onPanelStateChange && onPanelStateChange('minimized')}
              entityName={entityName}
              entityCategory={entityCategory}
            />
          </div>
          {sharedBanner}
          {sharedContent}
        </>
      )}
    </div>
  )
})

export default debugWrapper(ImmersiveMapPanelContent, 'ImmersiveMapPanelContent', 'src/components/map/ImmersiveMapPanelContent.jsx')
